import { useEffect, ReactNode } from 'react';
import { Link, useParams, useNavigate } from 'react-router-dom';
import PublicHeader from '../components/PublicHeader';
import './Blog.css';
import '../pages/Landing.css';

/* ====================================================================
   Blog post data — add new posts to this array
   ==================================================================== */

interface BlogSection {
  heading?: string;
  paragraphs: string[];
}

interface BlogPost {
  slug: string;
  title: string;
  excerpt: string;
  author: string;
  date: string;
  readTime: string;
  sections: BlogSection[];
}

export const blogPosts: BlogPost[] = [
  // ---- Add blog posts here ----
];

/* ====================================================================
   Shared shell — nav + footer used by both views
   ==================================================================== */

function BlogShell({ children }: { children: ReactNode }) {
  return (
    <div className="landing-page blog-page">
      <PublicHeader />

      {children}

      <footer className="landing-footer">
        <div className="landing-container footer-inner">
          <div className="footer-brand">
            <img src="/mosaic-icon.png" alt="mosaime" className="logo-icon-img footer-logo-icon-img" />
            <span>mosaime</span>
          </div>
          <div className="footer-links">
            <Link to="/features">Features</Link>
            <Link to="/how-it-works">How It Works</Link>
            <Link to="/philosophy">Philosophy</Link>
            <Link to="/faq">FAQ</Link>
            <Link to="/blog">Blog</Link>
            <Link to="/login">Log in</Link>
            <Link to="/signup">Sign up</Link>
          </div>
          <p className="footer-copy">&copy; {new Date().getFullYear()} MOSAIME LLC. All rights reserved.</p>
        </div>
      </footer>
    </div>
  );
}

/* ====================================================================
   Blog listing (index) page
   ==================================================================== */

function BlogIndex() {
  const formatDate = (dateStr: string) => {
    const d = new Date(dateStr + 'T00:00:00');
    return d.toLocaleDateString('en-US', { month: 'long', day: 'numeric', year: 'numeric' });
  };

  return (
    <BlogShell>
      <header className="blog-header">
        <div className="landing-container">
          <h1>Wellness Blog</h1>
          <p>Insights and inspiration for your wellness journey</p>
        </div>
      </header>

      <div className="landing-container blog-list-container">
        <div className="blog-grid">
          {blogPosts.map((post) => (
            <article key={post.slug} className="blog-card">
              <div className="blog-card-content">
                <h2>{post.title}</h2>
                <p>{post.excerpt}</p>
                <div className="blog-card-meta">
                  <span>{post.author}</span>
                  <span>·</span>
                  <span>{formatDate(post.date)}</span>
                  <span>·</span>
                  <span>{post.readTime}</span>
                </div>
                <Link to={`/blog/${post.slug}`} className="read-more">
                  Read More →
                </Link>
              </div>
            </article>
          ))}
        </div>
      </div>
    </BlogShell>
  );
}

/* ====================================================================
   Blog article (detail) page
   ==================================================================== */

export function BlogArticle() {
  const { slug } = useParams<{ slug: string }>();
  const navigate = useNavigate();
  const post = blogPosts.find((p) => p.slug === slug);

  useEffect(() => {
    window.scrollTo(0, 0);
  }, [slug]);

  if (!post) {
    return (
      <BlogShell>
        <div className="blog-not-found">
          <h2>Post not found</h2>
          <p>The article you're looking for doesn't exist.</p>
          <button className="btn hero-cta" onClick={() => navigate('/blog')}>
            Back to Blog
          </button>
        </div>
      </BlogShell>
    );
  }

  const formatDate = (dateStr: string) => {
    const d = new Date(dateStr + 'T00:00:00');
    return d.toLocaleDateString('en-US', { month: 'long', day: 'numeric', year: 'numeric' });
  };

  return (
    <BlogShell>
      <article className="blog-article">
        <header className="blog-article-header">
          <div className="landing-container blog-article-container">
            <Link to="/blog" className="blog-back-link">← Back to Blog</Link>
            <h1>{post.title}</h1>
            <div className="blog-article-meta">
              <span>{post.author}</span>
              <span>·</span>
              <span>{formatDate(post.date)}</span>
              <span>·</span>
              <span>{post.readTime}</span>
            </div>
          </div>
        </header>

        <div className="landing-container blog-article-container">
          <div className="blog-article-body">
            {post.sections.map((section, si) => (
              <section key={si} className="blog-section">
                {section.heading && <h2>{section.heading}</h2>}
                {section.paragraphs.map((para, pi) => (
                  <p key={pi}>{para}</p>
                ))}
              </section>
            ))}
          </div>

          <div className="blog-article-cta">
            <h3>Ready to build your mosaic?</h3>
            <Link to="/signup" className="btn hero-cta">Get Started Free</Link>
          </div>
        </div>
      </article>
    </BlogShell>
  );
}

/* ====================================================================
   Default export — the listing page
   ==================================================================== */

export default BlogIndex;
